/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { i18n, I18nService, state, property, querySlot, touch } from '@cds/core/internal';
import styles from './split-handle.element.scss';
/**
 * Button Split
 *
 * ```typescript
 * import '@cds/core/internal-components/split-handle/register.js';
 * ```
 *
 * ```html
 * <cds-internal-split-handle>
 *  <input type="range" step="10" aria-label="resize" />
 * </cds-internal-split-handle>
 * ```
 * @beta
 * @element cds-internal-split-handle
 * @slot - For projecting range input
 */
let CdsInternalSplitHandle = class CdsInternalSplitHandle extends LitElement {
    constructor() {
        super(...arguments);
        this.i18n = I18nService.keys.actions;
        this.direction = 'vertical';
        this.readonly = false;
        this.focused = false;
        this.step = '1';
    }
    render() {
        return html `
      <div class="private-host">
        <slot></slot>
      </div>
    `;
    }
    connectedCallback() {
        super.connectedCallback();
        this.readonly = !this.range;
    }
    firstUpdated(props) {
        super.firstUpdated(props);
        if (this.range) {
            this.range.ariaLabel = this.range.ariaLabel ? this.range.ariaLabel : this.i18n.resize;
            this.range.addEventListener('focus', () => (this.focused = true));
            this.range.addEventListener('blur', () => (this.focused = false));
            this.addEventListener('cdsTouchStart', () => this.toughStart());
            this.addEventListener('cdsTouchEnd', () => this.touchEnd());
            this.addEventListener('cdsTouchMove', (e) => this.touchMove(e));
        }
    }
    toughStart() {
        this.focused = true;
        this.step = this.range.step;
        this.range.step = '1';
    }
    touchEnd() {
        this.focused = false;
        this.range.step = this.step;
    }
    touchMove(e) {
        const offset = this.direction === 'vertical' ? e.detail.offsetX : e.detail.offsetY;
        this.range.valueAsNumber = this.range.valueAsNumber + offset;
        requestAnimationFrame(() => this.range.dispatchEvent(new Event('input')));
    }
};
CdsInternalSplitHandle.styles = [styles];
__decorate([
    i18n()
], CdsInternalSplitHandle.prototype, "i18n", void 0);
__decorate([
    property({ type: String })
], CdsInternalSplitHandle.prototype, "direction", void 0);
__decorate([
    state({ type: Boolean, reflect: true })
], CdsInternalSplitHandle.prototype, "readonly", void 0);
__decorate([
    state({ type: Boolean, reflect: true })
], CdsInternalSplitHandle.prototype, "focused", void 0);
__decorate([
    state()
], CdsInternalSplitHandle.prototype, "step", void 0);
__decorate([
    querySlot('input')
], CdsInternalSplitHandle.prototype, "range", void 0);
CdsInternalSplitHandle = __decorate([
    touch()
], CdsInternalSplitHandle);
export { CdsInternalSplitHandle };
